import React, { useMemo, useRef, useState } from "react"
import { MenuItem } from "@mui/material"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchSubscriptionsForSupport,
    SubscriptionsForSupportResponse,
    SupportSubscriptionQuery,
} from "../subscriptionsApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { AmountUnit } from "../../../../../currency/currencyTypes"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { DateTime } from "luxon"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import {
    defaultSubscriptionsFilterOptionsList,
    FiltersDrawerSubscriptions,
    SubscriptionsFilterProps,
} from "../../../../../supplier/reporting/accounting/subscriptions/components/FiltersDrawersSubscriptions"
import useActiveFiltersCount from "../../../../../supplier/reporting/accounting/subscriptions/hooks/useActiveFiltersCount"
import { SubscriptionOrder } from "../../../../../company/reports/accounting/subscriptions/subscriptionsTypes"
import { SelectMerchantAutocomplete } from "../../../../../supplier/reporting/accounting/components/inputFilters/SelectMerchantAutocomplete"
import { useExportSubscriptions } from "../../../../../supplier/reporting/accounting/subscriptions/hooks/useExportSupplierSubscriptions"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const selectSubscriptions = (data: SubscriptionsForSupportResponse) => data?.orders || []

export function SupportSubscriptions() {
    const { l10n } = useAppLocalization()

    const [drawerOpen, setDrawerOpen] = useState(false)

    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<SubscriptionsFilterProps>({
        ...defaultSubscriptionsFilterOptionsList,
        startDate,
        endDate,
    })
    const { activeCount } = useActiveFiltersCount(filtersList)

    // An order id identifies the order uniquely, so the search must not be narrowed by the
    // period - the user searches by id precisely because the date is unknown.
    const isSearchingByOrderId = Boolean(filtersList.orderId)

    const subscriptionsResult = useApiQuery(fetchSubscriptionsForSupport, {
        queryName: SupportSubscriptionQuery.SUBSCRIPTIONS_FOR_SUPPORT_REPORTS,
        dependencies: {
            ...filtersList,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            subscriptionIds: filtersList.subscriptionIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: isSearchingByOrderId ? undefined : filtersList.startDate?.toISODate(),
            endDate: isSearchingByOrderId ? undefined : filtersList.endDate?.toISODate(),
        },
        select: selectSubscriptions,
    })

    const tableInstanceRef = useRef<MRT_TableInstance<SubscriptionOrder>>(null)

    const { ApiQueryTable, columnFactory } = useApiQueryTable(subscriptionsResult)

    const columns = useMemo<MRT_ColumnDef<SubscriptionOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("support-subscriptions-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("support-subscriptions-table-employee-column"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={row.original.customer.displayName}
                        subtitle1={row.original.customer.employeeId}
                        subtitle2={row.original.customer.email}
                    />
                ),
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("support-subscriptions-table-subscribed-column"),
                    accessorKey: "subscriptionDetails.validFrom",
                },
                (row) => row.subscriptionDetails.validFrom,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("support-subscriptions-table-unsubscribed-column"),
                    accessorKey: "subscriptionDetails.validTo",
                },
                (row) => row.subscriptionDetails.validTo,
            ),
            columnFactory.typeColumn({
                header: l10n.getString("support-subscriptions-table-payment-column"),
                id: "paymentDetails.paymentMethod",
                accessorFn: (row) => {
                    const paymentMethod = row.paymentDetails?.paymentMehod

                    if (paymentMethod) {
                        return l10n.getStringForEnum("purchases-payment-method", paymentMethod, paymentMethod)
                    }

                    return ""
                },
                Cell: ({ cell }) => {
                    return <p style={{ inlineSize: "8rem", overflowWrap: "break-word" }}>{cell.getValue<string>()}</p>
                },
            }),
            columnFactory.moneyColumnWithUnit(
                {
                    header: l10n.getString("support-subscriptions-table-amount-column"),
                    id: "amount",
                },
                (row) => row.amount,
                AmountUnit.MONTH,
            ),
        ],
        [l10n, columnFactory],
    )

    const { handleExportRows } = useExportSubscriptions(
        filtersList.startDate?.toISODate() || DateTime.now().toISODate(),
        filtersList.endDate?.toISODate() || DateTime.now().toISODate(),
    )

    return (
        <>
            <LocalizedStrict id="support-subscriptions-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPORT_ACCOUNTING_SUBSCRIPTION} title="Subscriptions">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-orders">
                            <Breadcrumb>Orders</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-subscriptions">
                            <Breadcrumb>Subscriptions</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                tableInstanceRef={tableInstanceRef}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="support-subscriptions-table-cancel-subscription">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`cancel-subscription-${row.original.id}`}
                        >
                            Cancel subscription
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="support-subscriptions-table-details">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`details-${row.original.id}`}
                        >
                            Details
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />

                        <StandardExportButton
                            onClick={() => {
                                handleExportRows(tableInstanceRef.current?.getRowModel().rows)
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter
                            value={{
                                startDate: filtersList.startDate,
                                endDate: filtersList.endDate,
                            }}
                            onChange={(newValue) => {
                                setFiltersList((prev) => ({
                                    ...prev,
                                    startDate: newValue.startDate,
                                    endDate: newValue.endDate,
                                }))
                            }}
                        />

                        <SelectMerchantAutocomplete
                            merchantId={filtersList.merchantId}
                            setMerchantId={(merchantId) =>
                                setFiltersList((prev) => ({
                                    ...prev,
                                    merchantId,
                                }))
                            }
                        />
                    </StandardTableFilter>
                )}
            />

            <FiltersDrawerSubscriptions
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
